import { useState } from 'react';
import { ActionIcon, Button, Group, LoadingOverlay, Paper, Stack, Table, Text, Title } from '@mantine/core';
import dayjs from 'dayjs';
import { Icon, LoadMorePagination, SuggestedModuleDeleteModal, SuggestedModuleModal } from '@/components';
import { type SuggestedModule, type SuggestedModuleCreateRequest } from '@/types/suggestedModules';
import { useSuggestedModules } from '@/hooks';
import { ACTION_ICON_SIZE } from '@/constants/suggestedModules';
import styles from './SuggestedModules.module.sass';

export const SuggestedModules = () => {
  const {
    items,
    total,
    isLoading,
    isLoadingMore,
    hasMore,
    isSaving,
    isDeleting,
    handleLoadMore,
    createModule,
    updateModule,
    deleteModule,
  } = useSuggestedModules();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedModule, setSelectedModule] = useState<SuggestedModule | null>(null);
  const [moduleToDelete, setModuleToDelete] = useState<SuggestedModule | null>(null);

  const handleEdit = (module: SuggestedModule) => {
    setSelectedModule(module);
    setIsModalOpen(true);
  };

  const handleCreate = () => {
    setSelectedModule(null);
    setIsModalOpen(true);
  };

  const handleSave = async (data: SuggestedModuleCreateRequest) => {
    let success = false;
    if (selectedModule) {
      success = await updateModule(selectedModule.id, data);
    } else {
      success = await createModule(data);
    }

    if (success) {
      setIsModalOpen(false);
    }
  };

  const handleDelete = async () => {
    if (!moduleToDelete) return;
    const success = await deleteModule(moduleToDelete.id);
    if (success) {
      setModuleToDelete(null);
    }
  };

  const renderRows = items.map((item) => (
    <Table.Tr key={item.id}>
      <Table.Td>{item.name}</Table.Td>
      <Table.Td style={{ textTransform: 'capitalize' }}>{item.moduleType.replace('_', ' ')}</Table.Td>
      <Table.Td style={{ textTransform: 'capitalize' }}>{item.displayStyle}</Table.Td>
      <Table.Td>
        <a href={item.url} target="_blank" rel="noopener noreferrer">
          {item.url}
        </a>
      </Table.Td>
      <Table.Td>{item.startDate ? dayjs(item.startDate).format('MMM DD YYYY hh:mm A') : '-'}</Table.Td>
      <Table.Td>{item.endDate ? dayjs(item.endDate).format('MMM DD YYYY hh:mm A') : '-'}</Table.Td>
      <Table.Td className={styles.actionsColumn}>
        <Group gap="xs">
          <ActionIcon aria-label="edit" variant="subtle" onClick={() => handleEdit(item)}>
            <Icon name="IconEdit" size={ACTION_ICON_SIZE} />
          </ActionIcon>
          <ActionIcon aria-label="delete" variant="subtle" color="red" onClick={() => setModuleToDelete(item)}>
            <Icon name="IconTrash" size={ACTION_ICON_SIZE} />
          </ActionIcon>
        </Group>
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <Stack mx="xxl" my="md" gap="md">
      <Group justify="flex-start">
        <Button leftSection={<Icon name="IconPlus" />} onClick={handleCreate}>
          New Suggested Module
        </Button>
      </Group>

      <Paper radius="md" pos="relative" p="xl">
        <Title order={3} mb="md">
          Suggested Modules
        </Title>
        <LoadingOverlay visible={isLoading} />
        <Table striped highlightOnHover withRowBorders withColumnBorders withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Name</Table.Th>
              <Table.Th>Type</Table.Th>
              <Table.Th>Display Style</Table.Th>
              <Table.Th>URL</Table.Th>
              <Table.Th>Start Date</Table.Th>
              <Table.Th>End Date</Table.Th>
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {renderRows}
            {!isLoading && items.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={7}>
                  <Text c="dimmed">No suggested modules found</Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Paper>

      <LoadMorePagination
        loaded={items.length}
        total={total}
        hasMore={hasMore}
        isLoading={isLoading}
        isLoadingMore={isLoadingMore}
        onLoadMore={handleLoadMore}
      />

      <SuggestedModuleDeleteModal
        opened={Boolean(moduleToDelete)}
        onClose={() => setModuleToDelete(null)}
        onConfirm={handleDelete}
        isDeleting={isDeleting}
      />

      <SuggestedModuleModal
        opened={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSave}
        initialData={selectedModule}
        isLoading={isSaving}
      />
    </Stack>
  );
};
